import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ArrowheadRightDarkIcon } from "../../../../../../assets";
import { OrganisationTypeConstants } from "../../../../../../constants";
import { CursorChangeProps, DataGridColumnCurrency, ResultType } from "../../../../../../models";
import { searchPipelineDeals, SearchPipelineDealsResponse } from "../../../../../../service/query";
import { ResultData } from "../../../../../../service/Shared";
import { useAuth } from "../../../../../../useAuth";
import { flattenObject } from "../../../../../../utils";
import { getDealListingRoute, getWorkflowTasksBoardRoute } from "../../../../../../utils/routes";
import {
  DataGridColumnDefinition,
  DataGridLinkCellFormatterData,
  dataGridMapFilterCriteria,
} from "../../../../../../widget";
import { IconCellFormatterData } from "../../../../../../widget/data/DataGrid/models";

interface UseRelatedDealsPanelProps {
  workflowTemplateUuid?: string;
}

interface UseRelatedDealsPanelReturnData {
  relatedDealsPanelRef: RefObject<HTMLDivElement>;
  isLoading: boolean;
  refresh: boolean;
  columns: DataGridColumnDefinition[];
  onChange: ({ filtering, paging, sorting }: CursorChangeProps) => Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }>;
}

const columns: DataGridColumnDefinition[] = [
  {
    name: "Deal",
    key: "dealLink",
    dataType: "string",
    formatter: "link",
    filterable: true,
    sortable: true,
  },
  {
    name: "Current stage",
    key: "cachedStage",
    dataType: "string",
    formatter: "defaultPill",
    filterable: true,
    sortable: true,
  },
  {
    name: "Type",
    key: "dealTypes",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: false,
  },
  {
    key: "leadUser.fullName",
    name: "Lead",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
    minWidth: 136,
  },
  {
    name: "Target value",
    key: "investmentValue",
    dataType: "number",
    formatter: "currency",
    currency: DataGridColumnCurrency.Gbp,
    filterable: true,
    sortable: true,
  },
  {
    name: "Task board",
    key: "taskBoardLink",
    dataType: "string",
    formatter: "icon",
    alignment: "center",
    filterable: false,
    sortable: false,
    minWidth: 100,
  },
];

const pageSize = 10;

export const useRelatedDealsPanel = ({
  workflowTemplateUuid,
}: UseRelatedDealsPanelProps): UseRelatedDealsPanelReturnData => {
  const navigate = useNavigate();
  const { currentOrganisationUuid, currentUserType } = useAuth();

  const relatedDealsPanelRef = useRef<HTMLDivElement>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [refresh, setRefresh] = useState(true);

  const formatData = useCallback(
    (responseData: SearchPipelineDealsResponse | undefined): ResultData[] =>
      responseData?.results?.map((deal) => {
        const result = flattenObject(deal) as ResultData;

        result.dealLink = {
          text: deal.name,
          to: getDealListingRoute(deal.uuid, OrganisationTypeConstants.ASSET_MANAGER),
        } as DataGridLinkCellFormatterData;

        result.dealTypes = deal.types.map((type) => type.name).join(" | ");

        result.taskBoardLink = {
          action: () => {
            if (deal.workflow.workflowUuid) {
              navigate(getWorkflowTasksBoardRoute(deal.workflow.workflowUuid, currentUserType));
            }
          },
          icon: <ArrowheadRightDarkIcon width={12} height={12} />,
        } as IconCellFormatterData;

        return result;
      }) || [],
    [navigate, currentUserType]
  );

  const onChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    setIsLoading(true);

    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    if (!currentOrganisationUuid || !workflowTemplateUuid) {
      setIsLoading(false);
      return data;
    }

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    if (filterCriteria.dealLink) {
      filterCriteria.name = filterCriteria.dealLink;
      delete filterCriteria.dealLink;
    }

    if (filterCriteria.dealTypes) {
      filterCriteria.types = {
        some: {
          name: filterCriteria.dealTypes,
        },
      };
      delete filterCriteria.dealTypes;
    }

    if (filterCriteria["leadUser.fullName"]) {
      filterCriteria.leadUser = {
        fullName: filterCriteria["leadUser.fullName"],
      };
      delete filterCriteria["leadUser.fullName"];
    }

    filterCriteria.workflow = {
      workflowTemplateUuid: {
        operator: "eq",
        value: workflowTemplateUuid,
      },
    };

    const sortKeyMap: Record<string, string> = {
      dealLink: "name",
      "leadUser.fullName": "leadUser.fullName",
    };

    await searchPipelineDeals({
      paging: {
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
        limit: pageSize,
      },
      /* eslint-disable @typescript-eslint/no-explicit-any */
      sort: sorting.map((sort: { key: any; direction: any }) => ({
        key: sortKeyMap[sort.key] || sort.key,
        direction: sort.direction,
      })),
      /* eslint-enable */
      organisationUuid: currentOrganisationUuid,
      filter: { results: filterCriteria },
    }).then((response) => {
      data = {
        resultData: formatData(response.data),
        paging: {
          startCursor: response.data?.paging?.startCursor || "",
          endCursor: response.data?.paging?.endCursor || "",
          pageSize,
          totalCount: response.data?.paging?.total || 0,
          hasNextPage: response.data?.paging?.hasNextPage || false,
          hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
        },
      };
    });

    setIsLoading(false);
    return data;
  };

  useEffect(() => {
    if (workflowTemplateUuid) {
      setRefresh((prev) => !prev);
      relatedDealsPanelRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [workflowTemplateUuid]);

  return {
    relatedDealsPanelRef,
    isLoading,
    refresh,
    columns,
    onChange,
  };
};
